import React, { useState } from "react";
import { Button, Modal } from "antd";
import styled from "styled-components";

import LocalAssetsViewer from "../../common/LocalAssetsViewer";
import { Asset } from "../../../types";
import { EmailDocument } from "../../../api/graphql";
import { useDeleteEmailDocumentMutation } from "../../../api/graphql/mutations/deleteEmailDocument";
import { useUpdateEmailDocumentMutation } from "../../../api/graphql/mutations/updateEmailDocument";
import DeleteButton from "../../common/DeleteButton";
import { texts } from "../../common/DeleteAssetButton";

import {
  destroyMessage,
  showErrorMessage,
  showSuccessMessage,
  showLoadingMessage,
} from "../../../utils";

const AssetPreview = styled.div`
  display: flex;
  flex: 1;
  position: relative;
`;

const BottomButtonWrapper = styled.div`
  position: absolute;
  right: 5px;
  bottom: 5px;
  display: flex;
  gap: 8px;
`;

const ActionButton = styled(Button)``;

const ButtonWrapper = styled.div`
  position: absolute;
  margin: 90px 0 0 50px;
`;

export const getAsset = (emailDocument: EmailDocument) =>
  ({
    id: emailDocument.id,
    fullsize: emailDocument.url,
    filetype: emailDocument?.filename.split(".").pop(),
  } as Asset);

const Preview = ({
  emailDocument,
  fetchNext,
}: {
  emailDocument: EmailDocument;
  fetchNext: () => void;
}) => {
  const [deleteEmailDocumentMutation] = useDeleteEmailDocumentMutation();
  const [updateEmailDocumentMutation] = useUpdateEmailDocumentMutation();
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingToCustomer, setIsSendingToCustomer] = useState(false);

  const deleteEmailDocument = async () => {
    const DELETION_KEY = "delete-email-document";

    setIsLoading(true);
    try {
      showLoadingMessage(DELETION_KEY);
      const response = await deleteEmailDocumentMutation({
        variables: {
          id: emailDocument.id,
        },
      });

      if (!response.data?.deleteEmailDocument.success) {
        throw new Error("Error during deletion of email document");
      }
      showSuccessMessage("Deletion successful");
      fetchNext();
    } catch (error) {
      showErrorMessage("Error during deletion");
    } finally {
      destroyMessage(DELETION_KEY);
      setIsLoading(false);
    }
  };

  const sendToCustomer = async () => {
    setIsSendingToCustomer(true);
    try {
      await updateEmailDocumentMutation({
        variables: {
          id: emailDocument.id,
          manualMatchRequired: true,
        },
      });
      fetchNext();
    } catch (error) {
      showErrorMessage("Error during marking as manual match required");
    } finally {
      setIsSendingToCustomer(false);
    }
  };

  const handleSendToCustomerClick = () => {
    Modal.confirm({
      title: "Beleg an Kunden senden",
      content:
        "Dadurch wird ein To-Do für den Kunden erstellt, um den Beleg zuzuordnen.",
      onOk: sendToCustomer,
      okText: "Bestätigen & Senden",
      cancelText: "Abbrechen",
    });
  };

  return (
    <AssetPreview>
      <LocalAssetsViewer assets={[getAsset(emailDocument)]} minHeight={850} />
      <BottomButtonWrapper>
        {!emailDocument?.manualMatchRequired && (
          <ActionButton
            size="large"
            type="default"
            onClick={handleSendToCustomerClick}
            loading={isSendingToCustomer}
          >
            Send to Customer
          </ActionButton>
        )}
        <ActionButton
          data-test="nextBtn"
          type="primary"
          size="large"
          onClick={fetchNext}
        >
          Next
        </ActionButton>
      </BottomButtonWrapper>
      <ButtonWrapper>
        <DeleteButton
          isLoading={isLoading}
          handleConfirm={deleteEmailDocument}
          title={texts.title}
          okText={texts.okText}
          cancelText={texts.cancelText}
        />
      </ButtonWrapper>
    </AssetPreview>
  );
};

export default Preview;
